import {Component, OnChanges, OnDestroy, OnInit} from "@angular/core";
import {Subscription} from "rxjs";

import {ObjectBaseComponent} from "../../../../../core/shared/object/mvs-object-base/object-base.component";
import {DtoList} from "../../../../../core/shared/dto/dto.list";
import {WidgetData} from "../../../../../core/shared/widget/widget-data";
import {WidgetFactory} from "../../../../../core/shared/widget/service/widget.factory";
import {WidgetDataParam} from "../../../../../core/shared/widget/widget-data-param";
import {WidgetHelperButton} from "../../../../../core/shared/basic/widget-helper-button";
import {DtoImportObjectContext} from "../../../../../core/shared/dto/dto.import.object.context";
import {ObjectRequestList} from "../../../../../core/shared/dto/object/request/object-request-list";
import {ObjectRequestComplex} from "../../../../../core/shared/dto/object/request/object-request-complex";
import {ObjectRequestComplexNode} from "../../../../../core/shared/dto/object/request/object-request-complex-node";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";
import {Sorting} from "../../../../../core/shared/misc/sorting";
import {ObjectIdentifier} from "../../../../../core/shared/basic/object-identifier";
import {ObjectIdentifierData} from "../../../../../core/shared/basic/object-identifier-data";

import {CallDto} from "../../../model/dto/entity/call.dto";
import {CallProtocolDto} from "../../../model/dto/entity/call-protocol.dto";
import {CallProtocolStatusEnum} from "../../../enum/call-protocol-status.enum";

/** Which of the two states the lower area of this component is in. */
export type PcCallProtocolAreaState = 'list' | 'edit';

/** Toolbar action of the protocol worklist. */
const ACTION_CREATE_PROTOCOL = 'pc.call.protocol.create';

/** Entity of the protocols shown in the lower area. */
const OBJECT_TYPE_CALL_PROTOCOL = 'pc.CallProtocol';

/**
 * Call object component of {@code pc.Call}.
 *
 * <h3>Layout: two states, and the whole component follows them</h3>
 * State 'list' shows the call data widget above the protocol worklist. State 'edit' shows the protocol
 * component ALONE — the call data widget steps aside with the list, because writing a protocol is what
 * the advisor needs the height for, and the call header is not what they are reading at that moment:
 *
 * <pre>
 *   State 'list'                          State 'edit'
 *   ┌ Anruf (Daten) ───────┐              ┌ [← Zurück] Protokoll ─────┐
 *   ├ Protokolle ──────────┤   [+] / row  │ [✓ Abschließen]           │
 *   │ 08.08. in Bearbeitung│  ─────────▶  │ ( ) Eingabe   (•) KI      │
 *   │ 07.08. abgeschlossen │  ◀─ Zurück ─ │ … Editor oder KI-Dialog … │
 *   └──────────────────────┘              └───────────────────────────┘
 * </pre>
 *
 * Neither the data widget nor the list is visible in state 'edit' — {@link PcCallProtocolComponent}
 * has the component to itself there.
 * The transitions are: the toolbar's plus button opens 'edit' in the create case (protocol id null,
 * call reference handed in through the default DTO), a row click opens 'edit' on that protocol, and
 * the component's back output returns to 'list' with a freshly built, therefore reloaded, list.
 *
 * <h3>The single open protocol opens itself</h3>
 * A call that has EXACTLY ONE protocol and that protocol is not completed yet is the overwhelmingly
 * common case, and a one-row worklist the advisor has to click through is pure friction. So on load
 * that one protocol opens directly in state 'edit' — see {@link #evaluateProtocolAutoOpen}. Every
 * other constellation (no protocol, several protocols, or the single one already completed) keeps
 * the list, because there a decision is actually being made.
 *
 * The auto-open fires ONCE per call, never on the way back from state 'edit'. Repeating it there
 * would re-open the protocol the advisor just left and lock them out of their own worklist.
 *
 * <h3>One build path for the worklist</h3>
 * The worklist is configured in exactly one place, {@link #buildProtocolWorklist}, used by the first
 * build AND by the return from state 'edit'. Splitting it is what broke the plus button once already
 * — see the method's own note.
 *
 * <h3>Why a new widget alias</h3>
 * The former alias {@code pc.call.protocols.list.new} carries a stored, tenant-wide
 * {@code ui.UiWidgetLayout} with {@code objectWidget=object} and {@code formQuickAdd=true}. A stored
 * layout OVERRIDES the code parameters (mvs-widget.component.ts:441, mixWidgetConfigurationToRuntime),
 * so that alias renders a full editable form per row no matter what this file asks for. The layout is
 * deliberate and stays untouched; the reworked list therefore uses the fresh alias
 * {@code pc.call.protocols.worklist}, which has no layout yet, so the type provider defaults apply and
 * the parameters below take effect. {@code objectWidget} is set explicitly all the same, so the
 * intention is readable in the code and not only in the database.
 *
 * Round-based LLM dialog — design: projects/pc_call_obc/REQUIREMENT.md §8.3, §9 rule 1.
 */
@Component({
    selector: 'mvs-pc-call',
    templateUrl: './pc-call.component.html',
    standalone: false
})
export class PcCallComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;  // indicator whether the component is busy
    initialized: boolean; // indicator whether the component was initialized

    dto: CallDto;

    widgetObject: WidgetData;
    widgetCallProtocols: WidgetData;

    callObjectContext: DtoImportObjectContext;

    /** Which of the two states the protocol area is in. */
    protocolAreaState: PcCallProtocolAreaState = 'list';

    /**
     * The protocol under edit, or null for the create case.
     *
     * Deliberately NOT updated when the protocol component persists its new row: rebinding it would
     * make {@code ObjectComponent.ngOnChanges} (object.component.ts:233-256) destroy and recreate the
     * component the advisor is currently typing in. The created row reaches the list through the
     * reload on the way back instead.
     */
    editProtocolId: number = null;

    /** Carries the call reference into the create case through the native defaultCreateDto channel. */
    protocolDefaultCreateDto: CallProtocolDto = null;

    /**
     * Toolbar of the protocol worklist — the plus button that opens the create case.
     *
     * Deliberately WITHOUT an initializer: it is assigned by {@link #buildProtocolWorklist} together
     * with {@link #widgetCallProtocols}, so the two can never exist independently. An empty array
     * here would be truthy and would render a toolbar with no buttons in it — a silent version of
     * exactly the defect this pairing exists to prevent.
     */
    protocolListButtons: WidgetHelperButton[];

    /**
     * The call the auto-open has already been evaluated for.
     *
     * A plain boolean would be reset in {@link #onObjectChanged} and would therefore run again on every
     * reload of the SAME call — including the reload that follows a change made in the protocol — which
     * is exactly the "user cannot stay in the list" defect the once-per-call rule exists to prevent.
     * Keyed by call id, a different call still gets its own evaluation.
     */
    protected protocolAutoOpenCallId: number = null;

    /**
     * The protocol probe of {@link #evaluateProtocolAutoOpen}, so it can be cancelled.
     *
     * {@code ObjectComponent.ngOnChanges} (object.component.ts:233-256) destroys and recreates this
     * component whenever the shown call changes, so clicking quickly through calls would otherwise
     * leave an in-flight probe that switches a destroyed instance into state 'edit'.
     */
    protected subscriptions: Subscription[] = [];

    /**
     * On Object Change.
     */
    onObjectChanged() {

        this.protocolAreaState = 'list';
        this.editProtocolId = null;
        this.protocolDefaultCreateDto = null;

        this.uiRefresh();

    }

    uiRefresh() {

        const objectRequestList = ObjectRequestList.createBasic(true, [], []);

        objectRequestList.objectRequestComplex = ObjectRequestComplex.build(
            true,
            false,
            ObjectRequestComplexNode.createSimpleAttributeNode('callType'),
            ObjectRequestComplexNode.createSimpleAttributeNode('callSaleResult')
        );


        // WIDGET - CALL Data
        this.widgetObject = WidgetFactory.createWidgetEntityData(
            'pc.call.basic.data',
            'Anruf',
            'pc.Call',
            this.objectIdentifier.objectId,
            WidgetDataParam.create("size", "S")
        );

        this.widgetObject.dataProviderListRequest = objectRequestList;

        this.buildProtocolWorklist();

        this.evaluateProtocolAutoOpen();

    }

    /**
     * Opens the one open protocol of this call directly in state 'edit'.
     *
     * The condition is deliberately narrow: EXACTLY one protocol, and it is not
     * {@code CallProtocolStatusEnum.completed}. Then there is nothing for the advisor to choose and the
     * worklist would only cost a click. With none, with several, or with the single one already
     * completed, state 'list' stays — the advisor decides there whether to create, which one to open,
     * or whether to reopen a finished protocol at all.
     *
     * Runs ONCE per call, guarded by {@link #protocolAutoOpenCallId}, so the return from state 'edit'
     * lands in the list and stays there.
     *
     * The probe is a separate, small read and NOT the worklist widget's own load: the widget owns its
     * data provider (mvs-widget.component.ts:370) and hands nothing back to the host, and reading its
     * rows would mean waiting for a widget that this very method may be about to unmount.
     *
     * A failing probe is swallowed on purpose. The worklist is already built and fully operable at that
     * point, so the advisor simply gets the list — the pre-existing behaviour — instead of an error over
     * a pure convenience.
     */
    protected evaluateProtocolAutoOpen(): void {

        if (this.protocolAutoOpenCallId === this.dto.id) {
            return;
        }

        this.protocolAutoOpenCallId = this.dto.id;

        const crudService = this.coreService.getCrudService(OBJECT_TYPE_CALL_PROTOCOL);

        if (!crudService) {
            return;
        }

        const objectRequestList = ObjectRequestList.createBasic(
            false,
            [FilterCriteria.create("call", FilterCriteria.cOperatorEqual, this.dto.id)],
            []
        );

        const subscription = crudService.list(objectRequestList).subscribe({
            next: (protocolList: DtoList) => {

                if (protocolList?.entries?.length !== 1) {
                    return;
                }

                const protocol = <CallProtocolDto>protocolList.entries[0];

                if (protocol.protocolStatus === CallProtocolStatusEnum.completed) {
                    return;
                }

                // The advisor may have moved on while the probe was in flight — through the plus button
                // or a row click. Their choice wins.
                if (this.protocolAreaState !== 'list') {
                    return;
                }

                this.protocolDefaultCreateDto = null;
                this.editProtocolId = protocol.id;
                this.protocolAreaState = 'edit';

            },
            error: () => {
                // Convenience only — the worklist is already there. See the method note.
            }
        });

        this.subscriptions.push(subscription);

    }

    /**
     * Builds the protocol worklist COMPLETELY — the {@link WidgetData} AND every other thing the
     * {@code <mvs-widget>} binding of state 'list' reads.
     *
     * THE SINGLE BUILD PATH. Anything the list binding depends on is assigned here and nowhere else.
     *
     * This used to be two paths and they drifted apart: the toolbar buttons and the import context
     * were built once, in {@code uiRefresh()}, while the {@code WidgetData} was rebuilt on every
     * return from state 'edit'. The rebuild therefore produced a list whose toolbar configuration no
     * longer travelled with it — after "Zurück" the plus button was gone and no further protocol
     * could be created until the whole page was reloaded. Do not split this up again: if a new
     * binding is added to the worklist in the template, its value belongs in THIS method.
     *
     * A NEW {@code WidgetData} instance is what makes the widget reload, which is why this is called
     * again on the way back — that is how the created or changed protocol shows up in the list.
     *
     * No {@code functionCallbacks} are attached, and that is deliberate rather than an omission: the
     * widget's own creation paths are switched off. {@code disableUpdate} filters the generic
     * add/change/show menu entries out of the slide menu (mvs-widget.component.ts:311-314), and
     * {@code formQuickAdd} is not set, so {@code handleOpenQuickAdd} — the only path that honours a
     * {@code cancel: true} answer — is unreachable. The 'add' menu button of the list provider would
     * ignore that answer anyway and open a dialog (widget-type-provider-implementation-list.ts:
     * 224-232), which is precisely the second, contradicting creation path we removed. The plus
     * button below is the only way in.
     */
    protected buildProtocolWorklist(): void {

        // Context of the owning call. Derived purely from objectIdentifier, so rebuilding it costs
        // nothing and keeps this method self-sufficient.
        this.callObjectContext = DtoImportObjectContext.createFromObjectIdentifier(this.objectIdentifier);

        this.protocolListButtons = [
            {
                icon: 'fa-regular fa-plus',
                label: null,
                display: true,
                action: ACTION_CREATE_PROTOCOL,
                tooltip: 'Neues Protokoll anlegen',
                location: 'right'
            }
        ];

        // A plain selection list: each row is rendered READ-ONLY via objectWidget "data", because
        // writing a protocol happens in the protocol component that REPLACES this list.
        //
        // The parameter itself is mandatory and has no usable default on this path:
        // MvsWidgetListComponent#compValidateParameters rejects anything other than form/data/object and
        // then renders NOTHING, and createWidgetListEntityQl -> createQlWidget -> createWidgetList does
        // not inject one (only createWidgetListEntity and createWidgetListWithDetailEntity do).
        this.widgetCallProtocols = WidgetFactory.createWidgetListEntityQl(
            'pc.call.protocols.worklist',
            'Protokolle',
            'pc.CallProtocol',
            'Keine Protokolle vorhanden',
            [FilterCriteria.create("e.callId", FilterCriteria.cOperatorEqual, this.dto.id)],
            [new Sorting("e.createdDate", false)],
            false,
            WidgetDataParam.create("size", "S"),
            WidgetDataParam.create("selectionMode", "rowSelect"),
            WidgetDataParam.create("columns", "3"),
            WidgetDataParam.create("objectWidget", "data")
        );

    }

    // -------------------------------------------------------------------------
    // State transitions
    // -------------------------------------------------------------------------

    /**
     * Toolbar of the worklist. The plus button opens state 'edit' in the create case: no protocol id,
     * and the call reference in the default DTO. The protocol component writes the row itself the
     * moment it comes up, so from there on there is only edit.
     */
    onProtocolListToolbarButtonClick(button: WidgetHelperButton): void {

        if (button?.action !== ACTION_CREATE_PROTOCOL) {
            return;
        }

        const defaultCreateDto = new CallProtocolDto();
        defaultCreateDto.callDtoId = this.dto.id;

        this.protocolDefaultCreateDto = defaultCreateDto;
        this.editProtocolId = null;
        this.protocolAreaState = 'edit';

    }

    /**
     * Row selection in the worklist opens state 'edit' on that protocol.
     */
    onProtocolSelect(event: ObjectIdentifier | ObjectIdentifierData): void {

        const protocolId = event?.objectId ?? null;

        if (!protocolId) {
            return;
        }

        this.protocolDefaultCreateDto = null;
        this.editProtocolId = protocolId;
        this.protocolAreaState = 'edit';

    }

    /**
     * The protocol component reported "Zurück". Back to state 'list', with the worklist built again
     * — WHOLE, through the single build path — so the new or changed protocol is on screen and the
     * list comes back fully operable.
     */
    onProtocolBack(): void {

        this.protocolAreaState = 'list';
        this.editProtocolId = null;
        this.protocolDefaultCreateDto = null;

        this.buildProtocolWorklist();

    }

    /**
     * Cancels the auto-open probe still in flight. See {@link #subscriptions} for why this matters here.
     */
    override ngOnDestroy(): void {

        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }

        this.subscriptions = [];

        super.ngOnDestroy();

    }

}
